/**
 * Finding a quote the writer has since rewritten (decision 58: "if that text was rewritten, it fuzzy-searches for the
 * quote"; sync case table C7; part 39 log, AC4, AC7): the stretch of a text fewest edits (insertions, deletions,
 * substitutions of UTF-16 units) from the quote, within a bound of a quarter of its length, nearest a given place. Myers'
 * bit-vector algorithm (1999), 32 rows of the edit-distance table a word, so a search costs the stretch searched times the
 * quote's length over 32. Pure.
 */

/** AC4: quotes shorter than this aren't fuzzy-searched: a few edits would make them match almost anything. */
export const MIN_FUZZY_QUOTE = 8;

/** AC4: the most edits a fuzzy match may be from a quote of `length` units; null for a quote too short to search. */
export function fuzzyBound(length: number): number | null {
  return length < MIN_FUZZY_QUOTE ? null : Math.floor(length / 4);
}

/** A stretch found: `text[from, to)`, `edits` from the quote. */
export interface FuzzyMatch {
  readonly from: number;
  readonly to: number;
  readonly edits: number;
}

const WORD = 32;

/** Units below this find their rows in a table rather than a map: the text is mostly ASCII. */
const TABLE = 128;

/**
 * A pattern's rows per unit, word by word (Myers' `Peq`): units below {@link TABLE} in a table, the rest mapped. Words are
 * kept as signed 32-bit integers, which the engine holds as small integers where unsigned ones past 2^31 would be doubles;
 * the bits are the same.
 */
class Rows {
  readonly words: number;
  readonly length: number;
  readonly table: Int32Array;
  readonly #mapped = new Map<number, Int32Array>();
  readonly #none: Int32Array;

  constructor(pattern: string) {
    const words = Math.ceil(pattern.length / WORD);
    this.words = words;
    this.length = pattern.length;
    this.table = new Int32Array(TABLE * words);
    this.#none = new Int32Array(words);
    for (let row = 0; row < pattern.length; row += 1) {
      const unit = pattern.charCodeAt(row);
      const word = Math.floor(row / WORD);
      const bit = 1 << row % WORD;
      if (unit < TABLE) {
        this.table[unit * words + word] = Number(this.table[unit * words + word]) | bit;
        continue;
      }
      let bits = this.#mapped.get(unit);
      if (bits === undefined) {
        bits = new Int32Array(words);
        this.#mapped.set(unit, bits);
      }
      bits[word] = Number(bits[word]) | bit;
    }
  }

  /** The rows a unit at or past {@link TABLE} is in, word by word. */
  mapped(unit: number): Int32Array {
    return this.#mapped.get(unit) ?? this.#none;
  }
}

/**
 * One pass of Myers' algorithm (1999, the block version) over `text` from `start`, a unit at a time by `step` (+1 or −1),
 * `count` units: for each unit read, in order, the edit distance of the best match of all of the pattern ending at it.
 * `anchored` makes every match start where the reading starts (the table's top row counts up), as the search for a
 * match's start needs.
 */
function scan(rows: Rows, anchored: boolean, text: string, start: number, step: number, count: number): Int32Array {
  const words = rows.words;
  // Every row one more than the row above at the start: the table's first column is 0, 1, 2, …
  const plus = new Int32Array(words).fill(-1);
  const minus = new Int32Array(words);
  const last = 1 << (rows.length - 1) % WORD;
  const top = anchored ? 1 : 0;
  const table = rows.table;
  const scores = new Int32Array(count);
  let score = rows.length;
  for (let n = 0, at = start; n < count; n += 1, at += step) {
    const unit = text.charCodeAt(at);
    const bits = unit < TABLE ? table : rows.mapped(unit);
    const first = unit < TABLE ? unit * words : 0;
    let carry = top;
    for (let word = 0; word < words; word += 1) {
      const high = word === words - 1 ? last : 1 << 31;
      const pv = Number(plus[word]);
      const mv = Number(minus[word]);
      let eq = Number(bits[first + word]);
      const xv = eq | mv;
      if (carry < 0) eq |= 1;
      const xh = ((((eq & pv) + pv) | 0) ^ pv) | eq;
      let ph = mv | ~(xh | pv);
      let mh = pv & xh;
      const out = (ph & high) !== 0 ? 1 : (mh & high) !== 0 ? -1 : 0;
      ph <<= 1;
      mh <<= 1;
      if (carry < 0) mh |= 1;
      else if (carry > 0) ph |= 1;
      plus[word] = mh | ~(xv | ph);
      minus[word] = ph & xv;
      carry = out;
    }
    score += carry;
    scores[n] = score;
  }
  return scores;
}

/** What reading `units` units of text against a quote of `length` units costs, for a scan budget. */
export function fuzzyCost(units: number, length: number): number {
  return units * Math.max(1, Math.ceil(length / WORD));
}

/**
 * AC4: `pattern` (not empty) in `text[from, to)` within `bound` edits: the match with the fewest edits, then one not
 * ending on a space (a trimmed quote's match needn't take the space after it), then the one whose start is nearest
 * `hint`; null when none is within the bound. Its start is, among those as few edits away, one not on a space, then the
 * one nearest the match's end less the pattern's length.
 */
export function fuzzyFind(text: string, pattern: string, bound: number, from: number, to: number, hint: number): FuzzyMatch | null {
  const ends = scan(new Rows(pattern), false, text, from, 1, Math.max(0, to - from));
  let end = -1;
  let best: readonly number[] = [];
  ends.forEach((edits, n) => {
    if (edits > bound) return;
    const at = from + n;
    const key = [edits, text[at] === ' ' ? 1 : 0, Math.abs(at + 1 - pattern.length - hint)];
    if (end < 0 || before(key, best)) [end, best] = [at + 1, key];
  });
  if (end < 0) return null;
  // The start: read back from the end, anchored there, with the pattern's units reversed (as the text's are read).
  const lowest = Math.max(from, end - pattern.length - bound);
  const starts = scan(new Rows(pattern.split('').reverse().join('')), true, text, end - 1, -1, end - lowest);
  let start = end;
  let least: readonly number[] = [];
  starts.forEach((edits, n) => {
    const at = end - 1 - n;
    const key = [edits, text[at] === ' ' ? 1 : 0, Math.abs(end - at - pattern.length)];
    if (start === end || before(key, least)) [start, least] = [at, key];
  });
  return { from: start, to: end, edits: Number(best[0]) };
}

/** Whether `a` comes before `b`, compared item by item. */
function before(a: readonly number[], b: readonly number[]): boolean {
  for (let index = 0; index < a.length; index += 1) if (a[index] !== b[index]) return Number(a[index]) < Number(b[index]);
  return false;
}
